import './filename-formatters-page.css';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button, Dropdown, Modal, Space, Table, Tag, notification } from 'antd';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { AiOutlineEdit, AiOutlineDelete } from 'react-icons/ai';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { addControlStyle } from './filename-formatters-page-style';
import FilenameFormatterForm from './filename-formatter-form';

import { FilenameFormatter } from '../../model/filename-formatter-model';

import {
    useAddFilenameFormatterMutation,
    useDeleteFilenameFormatterMutation,
    useLazyGetFilenameFormattersQuery,
    useUpdateFilenameFormatterMutation,
} from '../../redux/api/filename-formatters';
import { useGetClientNamesQuery } from '../../redux/api/clients';

const formatterActionMenu = {
    updateMenu: { key: 'update', icon: <AiOutlineEdit />, label: 'Edit Formatter' },
    deleteMenu: { key: 'delete', icon: <AiOutlineDelete />, label: 'Delete Formatter' },
};

const FilenameFormattersPage = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const clientIdFilter = searchParams.get('clientId') ? Number(searchParams.get('clientId')) : undefined;

    const [formatters, setFormatters] = useState<FilenameFormatter[]>([]);
    const [formatter, setFormatter] = useState<FilenameFormatter>();
    const [isFormOpen, setFormOpen] = useState<boolean>(false);
    const [page, setPage] = useState<any>({ current: 1, pageSize: 25, total: 0 });
    const [tableHeight, setTableHeight] = useState(0);

    const [modal, contextHolder] = Modal.useModal();

    const { data: clientNames } = useGetClientNamesQuery(undefined);
    const [getFormatters] = useLazyGetFilenameFormattersQuery();
    const [postFormatter] = useAddFilenameFormatterMutation();
    const [putFormatter] = useUpdateFilenameFormatterMutation();
    const [removeFormatter] = useDeleteFilenameFormatterMutation();

    const updateTableHeight = () => {
        const windowHeight = window.innerHeight; // Full screen height
        const calculatedHeight = windowHeight - 288; // Subtract paddings and headers
        setTableHeight(calculatedHeight);
    };

    useEffect(() => {
        updateTableHeight(); // Set height on mount
        window.addEventListener("resize", updateTableHeight); // Adjust height on resize
        return () => window.removeEventListener("resize", updateTableHeight);
    }, []);

    useEffect(() => {
        fetchFormatters();
    }, [page.current, page.pageSize, clientIdFilter]);

    const fetchFormatters = async () => {
        const result = await getFormatters({
            index: page.current,
            viewableSize: page.pageSize,
            clientId: clientIdFilter,
        }).unwrap();

        const indexedFormatters = result.items.map((f: FilenameFormatter, index: number) => ({ ...f, key: index }));
        setFormatters(indexedFormatters);
        setPage((prev: any) => ({ ...prev, total: result.page.totalSize }));
    }

    const columns = [
        {
            title: 'Client',
            dataIndex: 'clientName',
            key: 'clientName',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 120,
            render: (type: string) => <Tag>{type}</Tag>,
        },
        {
            title: 'Formatter',
            dataIndex: 'formatter',
            key: 'formatter',
            ellipsis: true,
            render: (formatter: string) => <code>{formatter}</code>,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: 96,
            render: (_: any, record: FilenameFormatter) => {
                return <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [formatterActionMenu.updateMenu, formatterActionMenu.deleteMenu],
                        onClick: ({ key }) => handleFormatterAction(key, record)
                    }}>
                    <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                        <Space>
                            <span className="dropdown-title">
                                <BsThreeDotsVertical />
                            </span>
                        </Space>
                    </div>
                </Dropdown>
            }
        },
    ];

    const handleFormatterAction = (key: string, record: FilenameFormatter) => {
        setFormatter(record);
        switch (key) {
            case 'update': setFormOpen(true); break;
            case 'delete': onDelete(record); break;
        }
    }

    const openAddModal = () => {
        setFormatter(undefined);
        setFormOpen(true);
    }

    const saveFormatter = async (values: FilenameFormatter): Promise<void> => {
        try {
            if (values.id) {
                await putFormatter({ id: values.id, payload: values }).unwrap();
            } else {
                await postFormatter(values).unwrap();
            }

            fetchFormatters();
            setFormOpen(false);

            notification.success({
                message: values.id ? 'Filename Formatter Updated!' : 'Filename Formatter Created!',
                description: `The ${values.type} formatter has been saved.`
            });
        } catch (e: any) {
            notification.error({
                message: 'Saving Filename Formatter Failed!',
                description: e?.data ?? 'An error occurred while saving the filename formatter.'
            });
        }
    }

    const deleteFormatter = async (record: FilenameFormatter): Promise<void> => {
        try {
            await removeFormatter(record).unwrap();
            fetchFormatters();

            notification.success({
                message: 'Filename Formatter Deleted!',
                description: `The ${record.type} formatter has been deleted.`
            });
        } catch (e) {
            notification.error({
                message: 'Deleting Filename Formatter Failed!',
                description: 'An error occurred while deleting the filename formatter.'
            });
        }
    }

    const onDelete = (record: FilenameFormatter) => {
        modal.confirm({
            title: 'Delete the filename formatter',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure you want to delete this filename formatter?',
            okText: 'Ok',
            cancelText: 'Cancel',
            onOk: () => deleteFormatter(record),
        });
    }

    const clearClientFilter = () => {
        setSearchParams({});
        setPage((prev: any) => ({ ...prev, current: 1 }));
    }

    const handleTableChange = (pagination: any) => {
        setPage((prev: any) => ({
            ...prev,
            current: pagination.pageSize !== prev.pageSize ? 1 : pagination.current,
            pageSize: pagination.pageSize,
        }));
    }

    const filteredClientName = clientIdFilter
        ? (clientNames ?? []).find((c: any) => c.id === clientIdFilter)?.name
        : undefined;

    return <div className="filename-formatters-page">
        {contextHolder}
        <div style={addControlStyle}>
            <h1>Filename Formatters</h1>
            <Space>
                <Button type="primary" onClick={openAddModal}>
                    Create Filename Formatter
                </Button>
            </Space>
        </div>

        {clientIdFilter && (
            <div style={{ marginBottom: 16 }}>
                <Tag closable onClose={clearClientFilter} color="blue">
                    Filtered by client{filteredClientName ? `: ${filteredClientName}` : ` #${clientIdFilter}`}
                </Tag>
            </div>
        )}

        <Table
            dataSource={formatters}
            columns={columns}
            scroll={{ y: tableHeight }}
            pagination={{
                current: page.current,
                pageSize: page.pageSize,
                total: page.total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                showTotal: (total) => `${total} formatter${total === 1 ? '' : 's'}`,
            }}
            onChange={handleTableChange} />

        <Modal
            closable={false}
            open={isFormOpen}
            width={600}
            footer={null}
            destroyOnClose={true}
        >
            <FilenameFormatterForm
                formatter={formatter}
                defaultClientId={clientIdFilter}
                onClose={() => setFormOpen(false)}
                onSubmit={saveFormatter}
            />
        </Modal>
    </div>
};

export default FilenameFormattersPage;
